<template>
    <div>
        <div class="d-block d-sm-flex justify-content-sm-between">
            <button
                @click="goTo(Routes.create_user)"
                class="btn btn-primary waves-effect waves-light"
                type="button"
            >
                <i class="mdi mdi-plus"/>
                Create New User
            </button>
            <div class="d-flex mr-3 align-items-center">
                <p class="m-0 pr-2">Designate myself as enquiry handler</p>
                <input
                    v-model="dealer_principle_is_enquiry_handler"
                    type="checkbox"
                    :disabled="!dealer_principle_is_enquiry_handler && maxEnquiryHandlerReached"
                    @change="onDealerPrincipleIsEnquiryHandlerToggle"
                    style="width: 16px; height: 16px;"
                />
            </div>
        </div>
        <div class="mt-4">
            <div class="table-responsive">
                <table class="table table-hover mb-0">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th :width="180">Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="(users.data.length === 1 && hasRole(Roles.dealer_principle)) && users.meta.fetched">
                        <td :colspan="6">
                            <p class="text-center">No data...</p>
                        </td>
                    </tr>
                    <tr v-if="users.meta.loading">
                        <td :colspan="6">
                            <p class="text-center">Loading...</p>
                        </td>
                    </tr>
                    <tr
                        v-for="user in users.data"
                        v-if="user.id !== $store.state.user.id"
                    >
                        <td>{{ user.name }}</td>
                        <td>{{ user.email }}</td>
                        <td>{{ data_get(user, 'roles.0.name') }}</td>
                        <td>
                            <span v-if="!user.deleted_at">
                                <i class="mdi mdi-checkbox-blank-circle text-success"/>
                                Active
                            </span>
                            <span v-else>
                                <i class="mdi mdi-checkbox-blank-circle text-danger"/>
                                Suspended
                            </span>
                        </td>
                        <td>
                            <button
                                :disabled="busy || user.deleted_at"
                                @click="markUserAsEnquiryHandler(user)"
                                class="btn waves-effect waves-light"
                                v-b-tooltip.hover
                                :class="user.is_enquiry_handler ? 'btn-success' : 'btn-secondary'"
                                :title="user.is_enquiry_handler ? 'Remove as enquiry handler' : 'Mark as enquiry handler'"
                                type="button"
                            >
                                <i class="mdi mdi-account-multiple-check"/>
                            </button>
                            <button
                                :disabled="busy"
                                @click="goTo(Routes.view_user, {
                                    id: user.id
                                })"
                                class="btn btn-info waves-effect waves-light"
                                type="button"
                            >
                                <i class="mdi mdi-pencil"/>
                            </button>
                            <button
                                :disabled="busy"
                                @click="restoreUser(user)"
                                class="btn btn-success waves-effect waves-light"
                                type="button"
                                v-if="user.deleted_at"
                            >
                                <i class="mdi mdi-check"/>
                            </button>
                            <button
                                :disabled="busy"
                                @click="confirmDeleteUser(user)"
                                class="btn btn-danger waves-effect waves-light"
                                type="button"
                                v-else
                            >
                                <i class="mdi mdi-cancel"/>
                            </button>
                        </td>
                    </tr>
                    </tbody>
                </table>
                <pagination
                    :pagination="users.meta.pagination"
                    @current-change="onChangePage"
                    class="pt-2"
                />
            </div>
        </div>
        <el-dialog
            :visible.sync="reassign_delete_dialog"
            @closed="delete_form.clearFields()"
            center
            title="Confirm Suspend"
            :width="!is_mobile ? '30%' : '90%'"
        >
            <div class="text-center">
                Are you sure to suspend this user?
            </div>
            <div class="text-center">
                Please re-assign all user’s listing.
            </div>
            <div v-if="deleting_user">
                <select
                    :class="{ 'parsley-error' : delete_form.errors.has('re_assignee_id') }"
                    class="form-control mt-2"
                    title="re-assignee"
                    v-model="delete_form.re_assignee_id"
                >
                    <option :value="null">Select Assignee</option>
                    <option
                        :key="user.id"
                        :value="user.id"
                        v-for="user in users.data"
                        v-if="user.id !== deleting_user.id && !user.deleted_at"
                    >
                        {{ user.name }} {{ user.id === authUser.id ? '(Assign to me)' : '' }}
                    </option>
                </select>
                <ul :class="{'filled' : delete_form.errors.has('re_assignee_id')}"
                    class="parsley-errors-list">
                    <li class="parsley-required">
                        {{ delete_form.errors.get('re_assignee_id') }}
                    </li>
                </ul>
            </div>
            <span class="dialog-footer" slot="footer">
                <button
                    @click="reassign_delete_dialog = false"
                    class="btn btn-secondary waves-effect waves-light"
                    type="button"
                >
                    <i class="mdi mdi-close"/>
                    Cancel
                </button>
                <button @click="validateAndDelete(deleting_user)"
                        class="btn btn-danger waves-effect waves-light" type="button">
                    <i class="mdi mdi-checkbox-marked-circle-outline"/>
                    Confirm
                </button>
            </span>
        </el-dialog>
    </div>
</template>

<script>
import Roles from "@/constants/roles";
import Routes from "@/constants/routes";
import User from "@/models/user";
import { MAXIMUM_ENQUIRY_HANDLERS } from "@/constants/enquiry-handler";

export default {
    data() {
        return {
            Roles,
            Routes,
            users: {
                data: [],
                meta: {
                    fetched: false,
                    loading: false,
                    pagination: null
                }
            },
            enquiry_handlers: [],
            busy: null,
            company_id: null,
            deleting_user: null,
            reassign_delete_dialog: false,
            delete_form: new Form({
                re_assignee_id: null
            }, {
                re_assignee_id: 'required'
            }, {
                "required.re_assignee_id": "The re-assignee field is required"
            }),
            dealer_principle_is_enquiry_handler: false,
            enquiry_handler_count: 0,
        }
    },
        computed: {
            authUser() {
                return this.$store.state.user
            },
            activeUsers() {
                return _.filter(this.users.data, (user) => {
                    return !user.deleted_at;
                })
            },
            maxEnquiryHandlerReached() {
                return this.enquiry_handler_count >= MAXIMUM_ENQUIRY_HANDLERS;
            },
        },
        created() {
            this.dealer_principle_is_enquiry_handler = !!this.auth.user.is_enquiry_handler;
        },
        mounted() {
            this.initUsers();
        },
        methods: {
            fetchUsers(page) {
                this.users.meta.loading = true;
                let user = User
                    .include(
                        'roles',
                        'assignedVehicles'
                    )
                    .params({
                        all: true,
                    });
                if (page) {
                    user = user.page(page);
                }
                this.company_id = this.$route.params.company_id;
                if (this.company_id) {
                    user = user.where('company_id', this.company_id);
                }
                return user.get();
            },
            initUsers(page) {
                this.fetchUsers(page)
                    .then((response) => {
                        this.users.data = response.data;
                        this.users.meta.fetched = true;
                        this.users.meta.loading = false;
                        this.users.meta.pagination = response.meta;
                        this.enquiry_handler_count = _.filter(this.users.data, (user) => {
                            return user.is_enquiry_handler;
                        }).length;
                    });
            },
            confirmDeleteUser(user) {
                this.deleting_user = user;
                if (user.assigned_vehicles.length && this.activeUsers.length > 1) {
                    this.reassign_delete_dialog = true;
                } else {
                    this.$confirm('Are you sure to suspend this user?', 'Confirm', {
                        confirmButtonText: 'OK',
                        cancelButtonText: 'Cancel',
                        type: 'warning'
                    }).then(() => {
                        this.deleteUser(user);
                    });
                }
            },
            validateAndDelete(user) {
                this.delete_form.validate()
                    .then(() => {
                        this.deleteUser(user);
                    });
            },
            deleteUser(user) {
                this.busy = true;
                let params = this.delete_form.re_assignee_id
                    ? _.merge(
                        _.pick(this.delete_form, ['re_assignee_id']),
                        {
                            company_id: this.company_id
                        }
                    )
                    : null;

                User.delete(user.id, params)
                    .then(() => {
                        let re_assigned_user = _.find(this.users.data, {id: this.delete_form.re_assignee_id});
                        if (re_assigned_user) {
                            re_assigned_user.assigned_vehicles = this.deleting_user.assigned_vehicles;
                        }

                        this.deleting_user.deleted_at = new Date();
                        this.deleting_user.assigned_vehicles = [];

                        this.deleting_user = null;
                        this.reassign_delete_dialog = false;
                        this.$notify({
                            title: 'Success',
                            message: 'Successfully suspended user.',
                            type: 'success'
                        });

                        let deleted_user = _.find(this.users.data, {id: user.id});

                        if (deleted_user.is_enquiry_handler) {
                            deleted_user.is_enquiry_handler = false;
                            this.enquiry_handler_count--;
                        }
                    })
                    .finally(() => {
                        this.busy = false;
                    });
            },
            restoreUser(user) {
                this.$confirm('Are you sure to restore this user?', 'Confirm', {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    type: 'warning'
                }).then(() => {
                    this.busy = true;
                    User.restore(user.id)
                        .then(() => {
                            let deleted_user = _.find(this.users.data, {id: user.id});
                            if (deleted_user) {
                                deleted_user.deleted_at = null;
                            }
                            this.$notify({
                                title: 'Success',
                                message: 'Successfully restored user.',
                                type: 'success'
                            });
                        })
                        .finally(() => {
                            this.busy = false;
                        })
                });
            },
            markUserAsEnquiryHandler(user) {
                if (!user.is_enquiry_handler && this.enquiry_handler_count >= MAXIMUM_ENQUIRY_HANDLERS) {
                    this.$notify({
                        title: 'Warning',
                        message: 'Maximum enquiry handler limit reached. Please remove an existing enquiry handler to mark this user as enquiry handler.',
                        type: 'warning'
                    });
                    return;
                }

                let enquiry_handler_status = _.find(this.users.data, {id: user.id});
                let confirmation_message = enquiry_handler_status.is_enquiry_handler
                    ? `Are you sure to remove ${user.id === this.auth.user.id ? 'self' : 'this user'} from enquiry handlers?`
                    : `Are you sure to mark ${user.id === this.auth.user.id ? 'self' : 'this user'} as enquiry handler?`;

                this.$confirm(confirmation_message, 'Confirm', {
                    confirmButtonText: 'OK',
                    cancelButtonText: 'Cancel',
                    type: enquiry_handler_status.is_enquiry_handler ? 'warning' : 'info'
                }).then(() => {
                    this.busy = true;
                    axios.post(`/web-api/user/${user.id}/toggle-enquiry-handler-status`)
                    .then(() => {
                        enquiry_handler_status.is_enquiry_handler = !enquiry_handler_status.is_enquiry_handler;
                        let notification_message = enquiry_handler_status.is_enquiry_handler
                            ? `Successfully marked ${user.id === this.auth.user.id ? 'self' : 'user'} as enquiry handler.`
                            : `Successfully removed ${user.id === this.auth.user.id ? 'self' : 'user'} from enquiry handlers.`;

                        this.$notify({
                            title: 'Success',
                            message: notification_message,
                            type: 'success'
                        });

                        if (enquiry_handler_status.is_enquiry_handler) {
                            this.enquiry_handler_count +=1;
                        } else {
                            this.enquiry_handler_count -=1;
                        }   
                    })
                    .finally(() => {
                        this.busy = false;
                    })
                });
            },
            onChangePage(page) {
                this.initUsers(page)
            },
            goTo(route_name, params = {}) {
                this.$router.push({
                    name: route_name,
                    params,
                    query: {
                        company_id: this.company_id
                    }
                })
            },
            onDealerPrincipleIsEnquiryHandlerToggle() {
                this.markUserAsEnquiryHandler(this.auth.user);
            }
        }
    }
</script>

<style scoped>
thead {
  background-color: #15A9F0;
  color: #FFFFFF;
}
</style>
